import { ReportInterval } from '../types/reports';

/**
 * TS-Gegenstueck des Backend-Rechenkerns (D-08): reine Datumsarithmetik auf einem bereits
 * geladenen `configuration.interval`-Wert. Keine Serverrunde fuer "Januar minus ein Jahr".
 *
 * Namen und Reihenfolge spiegeln @see backend/src/Service/Reporting/ReportingPeriodCalculator.php:
 *   - periodStart      = calculateCurrentPeriod
 *   - previousPeriod    = calculatePreviousPeriod
 *   - nextPeriod        = calculateNextPeriod
 *   - periodEnd         = calculatePeriodEnd
 *   - indexInYear       = indexInYear
 *   - periodsPerYear    = periodsPerYear
 *   - previousYear      = previousYear
 *   - formatPeriod      = label
 *
 * Bewusst keine externe Datums-Bibliothek als Direktimport (die einzige heute installierte wird
 * im Frontend nur transitiv ueber `@mantine/dates` genutzt) -- eine neue Direktabhaengigkeit
 * wuerde den Stil spalten.
 *
 * Gerechnet wird auf Jahr-/Monatszahlen, NICHT auf `new Date(str)` mit lokalen Gettern -- sonst
 * verschiebt ein Zeitzonenversatz mit negativem Offset das Ergebnis um einen Tag. Die Zerlegung
 * erfolgt ueber die ersten zehn Zeichen des ISO-Strings und eine Musterpruefung.
 */

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MONTH_NAMES_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

interface YearMonth {
  year: number;
  month: number; // 1..12
}

/**
 * Zerlegt die ersten zehn Zeichen eines ISO-Datumsstrings in Jahr und Monat. Gibt `null` zurueck,
 * wenn die Eingabe nicht auf `YYYY-MM-DD` passt -- Aufrufer fallen dann auf die unveraenderte
 * Eingabe zurueck (T-22-17).
 */
function parseDate(dateString: string): YearMonth | null {
  const head = dateString.slice(0, 10);

  if (!DATE_PATTERN.test(head)) {
    return null;
  }

  const year = Number(head.slice(0, 4));
  const month = Number(head.slice(5, 7));

  return { year, month };
}

function formatDate(year: number, month: number, day: number): string {
  const yyyy = String(year).padStart(4, '0');
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

/** Letzter Tag eines Monats (1-basiert), ueber UTC, um lokale Zeitzonenverschiebung zu vermeiden. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function quarterStartMonth(month: number): number {
  return Math.floor((month - 1) / 3) * 3 + 1;
}

/**
 * Periodenanfang fuer das gegebene Intervall (Entsprechung zu `calculateCurrentPeriod`).
 * Passt die Eingabe nicht auf `YYYY-MM-DD`, wird sie unveraendert zurueckgegeben.
 */
export function periodStart(dateString: string, interval: ReportInterval): string {
  const parsed = parseDate(dateString);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY:
      return formatDate(year, month, 1);
    case ReportInterval.QUARTERLY:
      return formatDate(year, quarterStartMonth(month), 1);
    case ReportInterval.YEARLY:
      return formatDate(year, 1, 1);
  }
}

/**
 * Vorperiode (Entsprechung zu `calculatePreviousPeriod`): erst auf den Periodenanfang
 * normalisieren, dann einen Schritt zurueck. Passt die Eingabe nicht auf `YYYY-MM-DD`, wird sie
 * unveraendert zurueckgegeben.
 */
export function previousPeriod(dateString: string, interval: ReportInterval): string {
  const current = periodStart(dateString, interval);
  const parsed = parseDate(current);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY: {
      const targetMonth = month === 1 ? 12 : month - 1;
      const targetYear = month === 1 ? year - 1 : year;
      return formatDate(targetYear, targetMonth, 1);
    }
    case ReportInterval.QUARTERLY: {
      const targetMonth = month === 1 ? 10 : month - 3;
      const targetYear = month === 1 ? year - 1 : year;
      return formatDate(targetYear, targetMonth, 1);
    }
    case ReportInterval.YEARLY:
      return formatDate(year - 1, 1, 1);
  }
}

/**
 * Folgeperiode (Entsprechung zu `calculateNextPeriod`): erst auf den Periodenanfang
 * normalisieren, dann einen Schritt vor. Passt die Eingabe nicht auf `YYYY-MM-DD`, wird sie
 * unveraendert zurueckgegeben.
 */
export function nextPeriod(dateString: string, interval: ReportInterval): string {
  const current = periodStart(dateString, interval);
  const parsed = parseDate(current);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY: {
      const targetMonth = month === 12 ? 1 : month + 1;
      const targetYear = month === 12 ? year + 1 : year;
      return formatDate(targetYear, targetMonth, 1);
    }
    case ReportInterval.QUARTERLY: {
      const targetMonth = month === 10 ? 1 : month + 3;
      const targetYear = month === 10 ? year + 1 : year;
      return formatDate(targetYear, targetMonth, 1);
    }
    case ReportInterval.YEARLY:
      return formatDate(year + 1, 1, 1);
  }
}

/**
 * Letzter Tag der Periode, nicht der erste Tag der Folgeperiode (Entsprechung zu
 * `calculatePeriodEnd`). Monatslaenge kommt aus `daysInMonth`, damit Schaltjahre automatisch
 * richtig sind. Passt die Eingabe nicht auf `YYYY-MM-DD`, wird sie unveraendert zurueckgegeben.
 */
export function periodEnd(dateString: string, interval: ReportInterval): string {
  const current = periodStart(dateString, interval);
  const parsed = parseDate(current);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY:
      return formatDate(year, month, daysInMonth(year, month));
    case ReportInterval.QUARTERLY: {
      const endMonth = quarterStartMonth(month) + 2;
      return formatDate(year, endMonth, daysInMonth(year, endMonth));
    }
    case ReportInterval.YEARLY:
      return formatDate(year, 12, 31);
  }
}

/**
 * Index der Periode im Jahr, 1..12 / 1..4 / 1 (Entsprechung zu `indexInYear`). Normalisiert die
 * Eingabe selbst auf den Periodenanfang. Passt die Eingabe nicht auf `YYYY-MM-DD`, wird `NaN`
 * zurueckgegeben (kein sinnvoller Zahlwert fuer eine unlesbare Periode).
 */
export function indexInYear(dateString: string, interval: ReportInterval): number {
  const current = periodStart(dateString, interval);
  const parsed = parseDate(current);

  if (!parsed) {
    return NaN;
  }

  const { month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY:
      return month;
    case ReportInterval.QUARTERLY:
      return Math.ceil(month / 3);
    case ReportInterval.YEARLY:
      return 1;
  }
}

/** Anzahl der Perioden im Jahr: 12 / 4 / 1 (Entsprechung zu `periodsPerYear`). */
export function periodsPerYear(interval: ReportInterval): number {
  switch (interval) {
    case ReportInterval.MONTHLY:
      return 12;
    case ReportInterval.QUARTERLY:
      return 4;
    case ReportInterval.YEARLY:
      return 1;
  }
}

/**
 * Periodenanfang minus ein Jahr (Entsprechung zu `previousYear`), bewusst OHNE
 * `ReportInterval`-Parameter -- ein Periodenanfang ist immer der 1. eines Monats, Quartals oder
 * Jahres, "minus ein Jahr" liefert in jedem Fall wieder einen gueltigen Periodenanfang. Passt die
 * Eingabe nicht auf `YYYY-MM-DD`, wird sie unveraendert zurueckgegeben.
 */
export function previousYear(dateString: string): string {
  const parsed = parseDate(dateString);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;
  const day = Number(dateString.slice(8, 10));

  return formatDate(year - 1, month, day);
}

/**
 * Deutsche Periodenbeschriftung: monatlich "Januar 2026", quartalsweise "Q1 2026", jaehrlich
 * nacktes "2026" (Entsprechung zu `label`). Der Wortlaut ist mit dem PHP-`label()` identisch.
 * `ReportIntervalLabels` aus `types/reports.ts` bleibt unangetastet -- das ist die Beschriftung
 * des INTERVALLS ("Monatlich"), nicht der Periode.
 *
 * Zeitanteile im ISO-String werden ignoriert (nur die ersten zehn Zeichen zaehlen). Passt die
 * Eingabe nicht auf `YYYY-MM-DD`, wird sie unveraendert zurueckgegeben (uebernommener NaN-Guard
 * aus der robusteren der drei heutigen `DashboardPage`-Kopien).
 */
export function formatPeriod(dateString: string, interval: ReportInterval): string {
  const current = periodStart(dateString, interval);
  const parsed = parseDate(current);

  if (!parsed) {
    return dateString;
  }

  const { year, month } = parsed;

  switch (interval) {
    case ReportInterval.MONTHLY:
      return `${MONTH_NAMES_DE[month - 1]} ${year}`;
    case ReportInterval.QUARTERLY:
      return `Q${Math.ceil(month / 3)} ${year}`;
    case ReportInterval.YEARLY:
      return `${year}`;
  }
}
